/**
 * PATH: apps/qa/src/pages/new-inspection/ReviewStep.tsx
 * PURPOSE: Step 3 — review inspection details, structure summary, and create options
 */

import { Building2, ClipboardCheck, MapPin, CheckCircle2, Pencil, AlertTriangle } from 'lucide-react'
import { CATEGORY_INFO, PROJECT_TYPE_INFO } from '@/lib/inspection-constants'
import type { InspectionStepId, Job, FloorDraft } from './inspection-types'

interface ReviewStepProps {
  selectedJob: Job | null
  inspectionName: string
  category: 'residential' | 'commercial' | 'infrastructure'
  projectType: 'new_construction' | 'renovation' | 'remediation'
  buildingClasses: string[]
  tradeTags: string[]
  floors: FloorDraft[]
  totalAreas: number
  validationWarnings: string[]
  publishAfterCreate: boolean; setPublishAfterCreate: (v: boolean) => void
  saveAsTemplate: boolean; setSaveAsTemplate: (v: boolean) => void
  goToStep: (s: number) => void
  goToStepId: (id: InspectionStepId) => void
}

export default function ReviewStep(props: ReviewStepProps) {
  const {
    selectedJob, inspectionName, category, projectType,
    buildingClasses, tradeTags, floors, totalAreas,
    validationWarnings, publishAfterCreate, setPublishAfterCreate,
    goToStepId,
  } = props

  const criticalWarnings = validationWarnings.filter(w => !w.includes('you can add later'))
  const totalAttachments = floors.reduce((sum, f) => sum + f.areas.reduce((s, a) => s + a.attachments.length, 0), 0)
  const totalNotes = floors.reduce((sum, f) => sum + f.areas.filter(a => a.notes.trim()).length, 0)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-2">Review & Create</h2>
        <p className="text-muted-foreground text-sm">Review your inspection details before creating.</p>
      </div>

      {/* Validation Warnings */}
      {criticalWarnings.length > 0 && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <p className="font-medium text-amber-700 dark:text-amber-300 text-sm">Recommendations</p>
          </div>
          <ul className="space-y-1">
            {validationWarnings.map((warning, idx) => (
              <li key={idx} className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-amber-500" />{warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-4">
        {/* Job Summary */}
        <SummaryCard icon={Building2} title="Job" onEdit={() => goToStepId('job')}>
          <div className="flex items-center gap-3">
            <div>
              <p className="font-medium text-foreground">{selectedJob?.job_id ? `Job #${selectedJob.job_id}` : 'No job selected'}</p>
              <p className="text-sm text-muted-foreground">{selectedJob?.client || ''}{selectedJob?.site ? ` • ${selectedJob.site}` : ''}</p>
            </div>
          </div>
        </SummaryCard>

        {/* Details Summary */}
        <SummaryCard icon={ClipboardCheck} title="Inspection Details" onEdit={() => goToStepId('details')}>
          <dl className="space-y-2 text-sm">
            <SummaryRow label="Name" value={inspectionName || '—'} />
            <SummaryRow label="Category" value={CATEGORY_INFO[category]?.name} />
            <SummaryRow label="Project Type" value={PROJECT_TYPE_INFO[projectType]?.name} />
            <div className="flex justify-between items-start">
              <dt className="text-muted-foreground">Building Classes</dt>
              <dd className="text-foreground text-right flex flex-wrap gap-1 justify-end max-w-[60%]">
                {buildingClasses.length > 0 ? buildingClasses.map(cls => (
                  <span key={cls} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">{cls}</span>
                )) : <span className="text-muted-foreground">None</span>}
              </dd>
            </div>
            <div className="flex justify-between items-start">
              <dt className="text-muted-foreground">Trades</dt>
              <dd className="text-foreground text-right flex flex-wrap gap-1 justify-end max-w-[60%]">
                {tradeTags.length > 0 ? tradeTags.map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded text-xs font-medium">{tag}</span>
                )) : <span className="text-muted-foreground">None</span>}
              </dd>
            </div>
          </dl>
        </SummaryCard>

        {/* Structure Summary */}
        <SummaryCard icon={MapPin} title="Structure" onEdit={() => goToStepId('structure')}>
          {floors.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                {floors.length} floor{floors.length !== 1 ? 's' : ''}, {totalAreas} total areas
                {totalAttachments > 0 && ` • ${totalAttachments} file${totalAttachments !== 1 ? 's' : ''} attached`}
                {totalNotes > 0 && ` • ${totalNotes} note${totalNotes !== 1 ? 's' : ''}`}
              </p>
              <div className="space-y-2">
                {floors.map(floor => (
                  <div key={floor.id} className="text-sm flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-foreground">{floor.floor_name}</span>
                      {floor.areas.length > 0 ? (
                        <span className="text-muted-foreground"> — {floor.areas.map(a => {
                          const badges = []; if (a.attachments.length > 0) badges.push(`${a.attachments.length} files`); if (a.trade) badges.push(a.trade)
                          return a.area_name + (badges.length > 0 ? ` (${badges.join(', ')})` : '')
                        }).join(', ')}</span>
                      ) : <span className="text-amber-500 text-xs ml-2">(no areas)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No floors added yet. You can add them after creating the inspection.</p>
          )}
        </SummaryCard>

        {/* Options */}
        <div className="space-y-3">
          <label className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 border border-border cursor-pointer hover:bg-muted/70 transition-colors">
            <input type="checkbox" checked={publishAfterCreate} onChange={(e) => setPublishAfterCreate(e.target.checked)} className="w-5 h-5 rounded border-input accent-primary" />
            <div className="flex-1">
              <p className="font-medium text-foreground">Publish immediately</p>
              <p className="text-sm text-muted-foreground">Make this inspection available for data entry right away</p>
            </div>
          </label>
          <label className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 border border-border opacity-60">
            <input
              type="checkbox"
              checked={false}
              disabled
              aria-label="Save as template"
              title="Templates are not saved yet"
              className="w-5 h-5 rounded border-input accent-primary"
            />
            <div className="flex-1">
              <p className="font-medium text-foreground flex items-center gap-2">Save as template <span className="text-xs px-1.5 py-0.5 bg-amber-500/10 text-amber-600 rounded">Not saved yet</span></p>
              <p className="text-sm text-muted-foreground">Creating the inspection does not store a template.</p>
            </div>
          </label>
        </div>
      </div>
    </div>
  )
}

function SummaryCard({ icon: Icon, title, onEdit, children }: { icon: any; title: string; onEdit: () => void; children: React.ReactNode }) {
  return (
    <div className="p-4 rounded-xl bg-muted/50 border border-border">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-foreground flex items-center gap-2"><Icon className="w-4 h-4 text-primary" />{title}</h3>
        <button onClick={onEdit} className="text-xs text-primary hover:underline inline-flex items-center gap-1"><Pencil className="w-3 h-3" />Edit</button>
      </div>
      {children}
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground font-medium">{value || '—'}</dd>
    </div>
  )
}
